import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["overlay", "panel"]
  static values = {
    closeDuration: { type: Number, default: 240 },
  }

  connect() {
    this.boundEscape = this.handleEscape.bind(this)
    this.boundBeforeCache = this.beforeCache.bind(this)
    document.addEventListener("turbo:before-cache", this.boundBeforeCache)
    this.syncInitialOpenState()
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.boundBeforeCache)
    document.removeEventListener("keydown", this.boundEscape)
    this.forceClose()
  }

  open(event) {
    event?.preventDefault()
    if (!this.hasOverlayTarget) return
    if (this.closeTimer) window.clearTimeout(this.closeTimer)

    this.overlayTarget.hidden = false
    this.resetOverlayScroll()

    requestAnimationFrame(() => {
      this.overlayTarget.classList.add("is-open")
      if (this.hasPanelTarget) this.panelTarget.classList.add("is-open")
      this.resetOverlayScroll()
    })

    document.addEventListener("keydown", this.boundEscape)
    document.body.classList.add("modal-open")
    this.focusPanelWithoutScroll()
    this.dispatchLifecycleEvent("opened")
  }

  close(event) {
    event?.preventDefault()
    if (!this.hasOverlayTarget || this.overlayTarget.hidden) return

    this.overlayTarget.classList.remove("is-open")
    if (this.hasPanelTarget) this.panelTarget.classList.remove("is-open")

    if (this.overlayTarget.contains(document.activeElement)) {
      document.activeElement.blur()
    }

    this.closeTimer = window.setTimeout(() => {
      this.overlayTarget.hidden = true
      this.resetOverlayScroll()
      this.dispatchLifecycleEvent("closed")
    }, this.closeDurationValue)

    document.removeEventListener("keydown", this.boundEscape)
    document.body.classList.remove("modal-open")
  }

  closeIfBackdrop(event) {
    if (event.target === this.overlayTarget) this.close()
  }

  beforeCache() {
    this.forceClose()
  }

  handleEscape(event) {
    if (event.key === "Escape") this.close()
  }

  forceClose() {
    if (!this.hasOverlayTarget) return
    if (this.closeTimer) window.clearTimeout(this.closeTimer)

    this.overlayTarget.hidden = true
    this.overlayTarget.classList.remove("is-open")
    if (this.hasPanelTarget) this.panelTarget.classList.remove("is-open")
    document.body.classList.remove("modal-open")
    this.resetOverlayScroll()
    this.dispatchLifecycleEvent("closed")
  }

  syncInitialOpenState() {
    if (!this.hasOverlayTarget || this.overlayTarget.hidden) return

    this.overlayTarget.classList.add("is-open")
    if (this.hasPanelTarget) this.panelTarget.classList.add("is-open")
    document.body.classList.add("modal-open")
    document.addEventListener("keydown", this.boundEscape)
    this.focusPanelWithoutScroll()
  }

  resetOverlayScroll() {
    if (!this.hasOverlayTarget) return
    this.overlayTarget.scrollTop = 0
  }

  focusPanelWithoutScroll() {
    if (!this.hasPanelTarget) return

    try {
      this.panelTarget.focus({ preventScroll: true })
    } catch (_error) {
      this.panelTarget.focus()
    }
  }

  dispatchLifecycleEvent(state) {
    this.element.dispatchEvent(new CustomEvent(`modal:${state}`, { bubbles: true }))
  }
}
